import * as React from 'react';

import { DocumentType } from '@rematter/frontend-lib-api-client';
import { enumDocumentType } from '@rematter/frontend-lib-api-client';
import { Checkbox } from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import startCase from 'lodash/startCase';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { LoadRecord } from '~/modules/trade/types/load';

const DOCUMENT_TYPES = [
  enumDocumentType.Invoice,
  enumDocumentType.BillOfLading,
  enumDocumentType.PackingList,
];

interface Props {
  selectedDocuments: Record<string, Record<DocumentType, boolean>>;
  loads?: Pick<Partial<LoadRecord>, 'loadNumber'> & { id: string }[];
  onHeaderSelect: (
    documentType: DocumentType | 'all',
    checked: boolean,
  ) => void;
}

export const BookingTabDocumentsTableHeader: React.FC<Props> = ({
  selectedDocuments,
  loads,
  onHeaderSelect,
}) => {
  const t = useTranslations();

  const selectedLoadsCount = Object.keys(selectedDocuments).filter((loadId) =>
    DOCUMENT_TYPES.some((type) => selectedDocuments[loadId]?.[type]),
  ).length;

  const allSelected =
    selectedLoadsCount > 0 &&
    loads?.every((load) =>
      DOCUMENT_TYPES.every((type) => selectedDocuments[load.id]?.[type]),
    );

  const isIndeterminate =
    selectedLoadsCount > 0 &&
    !loads?.every((load) =>
      DOCUMENT_TYPES.every((type) => selectedDocuments[load.id]?.[type]),
    );

  const getDocumentTypeName = (type: DocumentType) => {
    const key = type
      .toLowerCase()
      .replace(/([A-Z])/g, (match) => match.toLowerCase()) as keyof typeof t;
    return t.bookingDetail.documents.types[key as never] ?? startCase(type);
  };

  return (
    <div className="@BookingTabDocumentsTableHeader flex w-full flex-row items-center gap-4 border-b border-neutral/10 py-2">
      <div className="flex h-[45px] w-[150px] items-center rounded-md bg-neutral/5 p-3">
        <Checkbox
          onChange={(checked) => onHeaderSelect('all', checked)}
          isIndeterminate={isIndeterminate}
          isSelected={allSelected || false}
        >
          <div className="text-base font-bold">
            {t.bookingDetail.documents.labels.allLoads}{' '}
            <span className="font-normal text-neutral/60">
              ・ {loads?.length ?? 0}
            </span>
          </div>
        </Checkbox>
      </div>
      {DOCUMENT_TYPES.map((type) => {
        const isChecked = loads?.every(
          (load) => selectedDocuments[load.id]?.[type],
        );
        const hasPartialSelection =
          loads?.some((load) => selectedDocuments[load.id]?.[type]) &&
          !loads?.every((load) => selectedDocuments[load.id]?.[type]);

        const getColumnWidth = (docType: DocumentType) => {
          switch (docType) {
            case enumDocumentType.BillOfLading:
            case enumDocumentType.PackingList:
              return 'w-[180px]';
            case enumDocumentType.Invoice:
              return 'w-[140px]';
            default:
              return 'w-[140px]';
          }
        };

        return (
          <div
            key={type}
            className={cn(
              'flex h-[45px] items-center gap-2 rounded-md p-3',
              getColumnWidth(type),
            )}
          >
            <Checkbox
              onChange={(checked) => onHeaderSelect(type, checked)}
              isIndeterminate={hasPartialSelection}
              isSelected={isChecked || false}
            >
              <div className="text-base font-semibold text-neutral/60">
                {getDocumentTypeName(type)}
              </div>
            </Checkbox>
          </div>
        );
      })}
    </div>
  );
};
